import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';

export default function SharePopup({ open, onClose, highestScore, onShare, shareFeedback, isSharing }) {
  const t = useTranslations('common.newspaper.art_games.color_mix.share_popup');
  const [hasCopied, setHasCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setHasCopied(false);
    }
  }, [open]);

  useEffect(() => {
    setHasCopied(false);
  }, [shareFeedback]);

  const scoreLabel = useMemo(() => {
    if (highestScore > 0) {
      return t('score.match', { score: highestScore.toFixed(1) });
    }

    return t('score.empty');
  }, [highestScore, t]);

  const eyebrow = highestScore > 0 ? t('eyebrow.highest_match') : t('eyebrow.challenge');

  const handleCopy = async () => {
    if (!shareFeedback?.link) {
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      return;
    }

    try {
      await navigator.clipboard.writeText(shareFeedback.link);
      setHasCopied(true);
    } catch (_error) {
      setHasCopied(false);
    }
  };

  const feedbackToneClass = shareFeedback?.tone ? `color-mix-share__feedback--${shareFeedback.tone}` : '';

  return (
    <GenericPopup
      open={open}
      onCloseAction={onClose}
      closeButtonText={t('close')}
      opacity
      className="color-mix-share__popup"
      backgroundClassName="color-mix-share__overlay"
    >
      <div className="color-mix-share">
        <header className="color-mix-share__header">
          <span className="color-mix-share__eyebrow">{eyebrow}</span>
          <h2 className="color-mix-share__title">{t('title')}</h2>
        </header>

        <div className="color-mix-share__visual" aria-hidden="true">
          <div className="color-mix-share__orbit color-mix-share__orbit--one" />
          <div className="color-mix-share__orbit color-mix-share__orbit--two" />
          <div className="color-mix-share__swatch color-mix-share__swatch--target" />
          <div className="color-mix-share__swatch color-mix-share__swatch--mix" />
        </div>

        <div className="color-mix-share__score">
          <span className="color-mix-share__score-label">{eyebrow}</span>
          <span className="color-mix-share__score-value">{scoreLabel}</span>
        </div>

        <p className="color-mix-share__description">
          {t('description')}
        </p>

        <ContinueButton className="color-mix-share__action" onClick={onShare} loading={isSharing}>
          {t('share_invite')}
        </ContinueButton>

        {shareFeedback ? (
          <div className={`color-mix-share__feedback ${feedbackToneClass}`}>
            <p className="color-mix-share__feedback-message">{shareFeedback.message}</p>
            {shareFeedback.link ? (
              <button type="button" className="color-mix-share__link" onClick={handleCopy}>
                {shareFeedback.link}
                {hasCopied && <span className="color-mix-share__copied">{t('copied')}</span>}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </GenericPopup>
  );
}

SharePopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  highestScore: PropTypes.number.isRequired,
  onShare: PropTypes.func.isRequired,
  shareFeedback: PropTypes.shape({
    message: PropTypes.string,
    link: PropTypes.string,
    tone: PropTypes.oneOf(['success', 'info', 'error']),
  }),
  isSharing: PropTypes.bool,
};

SharePopup.defaultProps = {
  shareFeedback: null,
  isSharing: false,
};
